import React, { Component } from 'react';
import Slider from 'react-slick';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import CosmaRealEstatePreview from './CosmaRealEstatePreview.jsx';
import {
  getLocationName,
  getPriceHistory,
  getRealEstateType,
} from '../../selectors/selectors.js';
import realEstateTypes from '../../realEstateTypes.js';
import { calculateDeltaInPercent } from '../../utils/NumbersHelper.js';
import getToAndFromPrice from '../../utils/priceStatCalculator.js';
import tracking from '../../tracking/tracking.js';
import { REAL_ESTATE_PREVIEW_CLICK } from '../../tracking/trackingNaming.js';
import actions from '../../actions.js';
import RIGHT_ARROW from '../../assets/images/chevron_right_new.svg';
import LEFT_ARROW from '../../assets/images/chevron_left_new.svg';
import style from './RealEstatePreview.less';

export class RealEstatePreviewWidget extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentSlide: 0,
      mounted: false,
    };
  }

  setCurrentSlide(currentSlide) {
    this.setState({ currentSlide });
  }

  componentDidMount() {
    this.setState({ mounted: true });
  }

  componentWillUnmount() {
    this.setState({ mounted: false });
  }

  static getSliderSettings = () => ({
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    prevArrow: <PrevButton />,
    nextArrow: <NextButton />,
  });

  scrollAndNavigate = (realEstateType) => {
    tracking.report(REAL_ESTATE_PREVIEW_CLICK);
    this.props.selectRealEstateType(realEstateType);
    this.props.updateUserIntent(realEstateType);
    window.scrollTo(0, 0);
  };

  navigateFromCurrentSlide = (stats) => {
    const { currentSlide } = this.state;
    this.scrollAndNavigate(stats[currentSlide].realEstateType);
  };

  getPreviewStatsForRealEstates = () => {
    const { priceHistory, locationName } = this.props;

    const previewStatsForRealEstates = [];
    Object.values(realEstateTypes).forEach((realEstateType) => {
      if (
        priceHistory &&
        priceHistory[realEstateType] &&
        priceHistory[realEstateType].length &&
        priceHistory[realEstateType][0].pricesPerQ
      ) {
        const [{ pricesPerQ }] = priceHistory[realEstateType];
        const { toPrice, fromPrice } = getToAndFromPrice(pricesPerQ);
        const priceChangeInPercent = Math.round(calculateDeltaInPercent(fromPrice.price, toPrice.price));
        previewStatsForRealEstates.push({
          locationName,
          realEstateType,
          currentStat: toPrice,
          priceTrend: {
            year: fromPrice.year,
            quarter: fromPrice.quarter,
            sign: priceChangeInPercent < 0 ? '' : '+',
            priceChangeInPercent,
          },
        });
      }
    });

    return previewStatsForRealEstates;
  };

  render() {
    const previewStats = this.getPreviewStatsForRealEstates();

    if (!previewStats || !previewStats.length) {
      return null;
    }

    const stats = this.getPreviewStatsForRealEstates().filter(
      (previewStat) => previewStat.realEstateType !== this.props.realEstateType,
    );

    return (
      <div className="border">
        <div className={`grid ${style.widgetMargin}`}>
          <div className="grid-item">
            {!this.state.mounted ? (
              <div style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
                {stats.map((previewStat, index) => (
                  <div key={index} data-identifier="realEstatePreview" data-serverside="true" className="inline-block one-whole">
                    <CosmaRealEstatePreview
                      {...previewStat}
                      onClickHandler={() => this.scrollAndNavigate(previewStat.realEstateType)}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <Slider
                {...RealEstatePreviewWidget.getSliderSettings()}
                beforeChange={(current, next) => this.setCurrentSlide(next)}
              >
                {stats.map((previewStat, index) => (
                  <div key={index} data-identifier="realEstatePreview">
                    <CosmaRealEstatePreview
                      {...previewStat}
                      onClickHandler={() => this.scrollAndNavigate(previewStat.realEstateType)}
                    />
                  </div>
                ))}
              </Slider>
            )}
          </div>
          <div className="grid-item align-right" style={{ minHeight: '20px' }}>
            <button
              id="external-learn-more"
              type="button"
              className="button-secondary desk-hide palm-hide margin-top-xl"
              onClick={() => this.navigateFromCurrentSlide(stats)}
            >
              Mehr erfahren
            </button>
          </div>
        </div>
      </div>
    );
  }
}

RealEstatePreviewWidget.propTypes = {
  priceHistory: PropTypes.object.isRequired,
  selectRealEstateType: PropTypes.func,
  updateUserIntent: PropTypes.func,
  realEstateType: PropTypes.string.isRequired,
};

const mapDispatchToProps = (dispatch) => ({
  selectRealEstateType: (value) => dispatch(actions.selectRealEstateType(value)),
  updateUserIntent: (value) => dispatch(actions.updateUserIntent(value)),
});

const mapStateToProps = (state) => ({
  priceHistory: getPriceHistory(state),
  realEstateType: getRealEstateType(state),
  locationName: getLocationName(state),
});

export default connect(mapStateToProps, mapDispatchToProps)(RealEstatePreviewWidget);

const NextButton = (props) => (
  <div
    id="next-arrow"
    className={`slick-default-arrow-hide margin-top-xl margin-right-m ${props.className}`}
    onClick={props.onClick}
    onKeyDown={(event)=> {
      if (event.key === 'ArrowRight'){
        props.onClick();
      }
    }}
    role="button"
    tabIndex={0}
  >
    <img className="margin-right-xl palm-hide" src={RIGHT_ARROW} alt="right-arrow" />
    <div className="margin-right-xxl mobile desk-hide lap-hide">
      <img src={RIGHT_ARROW} alt="right-arrow" />
    </div>
  </div>
);

const PrevButton = (props) => (
  <div
    className={`padding-left-s slick-default-arrow-hide margin-top-xl ${props.className}`}
    onClick={props.onClick}
    onKeyDown={(event)=> {
      if (event.key === 'ArrowLeft'){
        props.onClick();
      }
    }}
    role="button"
    tabIndex={0}
  >
    <img className="" src={LEFT_ARROW} alt="left-arrow" />
  </div>
);
